import { computed, onScopeDispose, ref, watch } from "vue";
import { defineStore } from "pinia";
import { LOCALE_KEY, normalizeLocale, readLocalePreference, setCurrentLocale } from "@/i18n/locales";

export type ThemePreference = "system" | "light" | "dark";

function readPreference(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function savePreference(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* Appearance still works when browser storage is unavailable. */ }
}

export const usePreferencesStore = defineStore("preferences", () => {
  const savedTheme = readPreference("gameglee.theme");
  const theme = ref<ThemePreference>(savedTheme === "light" || savedTheme === "dark" ? savedTheme : "system");
  const locale = ref<string>(readLocalePreference());
  const media = typeof matchMedia === "function" ? matchMedia("(prefers-color-scheme: dark)") : undefined;
  const systemDark = ref(media?.matches ?? false);
  const dark = computed(() => theme.value === "dark" || (theme.value === "system" && systemDark.value));

  const onSystemChange = (event: MediaQueryListEvent) => { systemDark.value = event.matches; };
  media?.addEventListener("change", onSystemChange);
  const onStorage = (event: StorageEvent) => {
    if (event.key === LOCALE_KEY || event.key === null) locale.value = readLocalePreference();
  };
  window.addEventListener("storage", onStorage);
  onScopeDispose(() => { media?.removeEventListener("change", onSystemChange); window.removeEventListener("storage", onStorage); });

  watch(theme, (value) => {
    savePreference("gameglee.theme", value);
  }, { immediate: true });
  watch(dark, (value) => {
    document.documentElement.dataset.theme = value ? "dark" : "light";
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", value ? "#101510" : "#fafbf8");
  }, { immediate: true });
  watch(locale, (value) => {
    const normalized = normalizeLocale(value);
    if (normalized !== value) { locale.value = normalized; return; }
    setCurrentLocale(value);
    document.documentElement.lang = value;
    document.documentElement.dir = value === "ar" ? "rtl" : "ltr";
  }, { immediate: true, flush: "sync" });

  return { theme, locale, dark };
});
